import React from 'react'

interface ProductCardSkeletonProps {
	className?: string
	showVariants?: boolean
}

const ProductCardSkeleton: React.FC<ProductCardSkeletonProps> = ({
	className = '',
	showVariants = true,
}) => {
	return (
		<div
			className={`group relative bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm animate-pulse ${className}`}
		>
			<div className='relative aspect-[3/2] sm:aspect-[4/3] md:aspect-[3/2] lg:aspect-[4/3] overflow-hidden bg-gray-50'>
				<div className='w-full h-full bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 animate-pulse' />

				<div className='absolute top-2 left-2 bg-gray-200 rounded-md shadow-sm z-10'>
					<div className='w-12 h-5 bg-gray-300 rounded animate-pulse' />
				</div>

				<div className='absolute top-2 right-2 bg-gray-200/80 backdrop-blur-sm rounded-md'>
					<div className='w-16 h-5 bg-gray-300 rounded animate-pulse' />
				</div>

				<div className='absolute top-10 right-2 bg-gray-200 rounded-md'>
					<div className='w-20 h-5 bg-gray-300 rounded flex items-center gap-1 px-2 py-1'>
						<div className='w-2 h-2 bg-gray-400 rounded-full animate-pulse' />
						<div className='w-12 h-2 bg-gray-400 rounded animate-pulse' />
					</div>
				</div>

				<div className='absolute top-2 left-2 z-20'>
					<div className='w-10 h-10 bg-gray-200 rounded-full shadow-sm mb-2 animate-pulse' />
				</div>

				<div className='absolute bottom-2 right-2'>
					<div className='w-11 h-11 bg-gray-200 rounded-full shadow-sm animate-pulse' />
				</div>
			</div>

			<div className='p-3 sm:p-4 space-y-2'>
				<div className='w-20 h-3 bg-gray-200 rounded animate-pulse' />

				<div className='space-y-1 min-h-[2.5rem] sm:min-h-[3rem] md:min-h-[2.5rem] lg:min-h-[3rem]'>
					<div className='w-full h-4 bg-gray-200 rounded animate-pulse' />
					<div className='w-3/4 h-4 bg-gray-200 rounded animate-pulse' />
				</div>

				<div className='flex items-center gap-1 sm:gap-1.5'>
					<div className='flex items-center gap-0.5'>
						{[...Array(5)].map((_, i) => (
							<div
								key={i}
								className='w-3 h-3 sm:w-3 sm:h-3 md:w-3 md:h-3 lg:w-4 lg:h-4 bg-gray-200 rounded-full animate-pulse'
							/>
						))}
					</div>
					<div className='w-16 h-3 bg-gray-200 rounded ml-1 animate-pulse' />
				</div>

				{showVariants && (
					<div className='space-y-1.5'>
						<div className='w-16 h-3 bg-gray-200 rounded animate-pulse' />

						<div className='flex gap-2 sm:gap-2.5 md:gap-2 lg:gap-2.5 flex-wrap'>
							{[...Array(3)].map((_, index) => (
								<div
									key={index}
									className={`w-24 h-24 rounded-xl border-2 bg-gradient-to-br from-gray-100 via-gray-200 to-gray-100 animate-pulse ${
										index === 0
											? 'border-gray-300 ring-2 ring-gray-300/20'
											: 'border-gray-200'
									}`}
								/>
							))}
							<div className='w-24 h-24 rounded-xl border-2 border-gray-200 bg-gray-100 flex items-center justify-center'>
								<div className='w-3 h-3 bg-gray-300 rounded animate-pulse' />
							</div>
						</div>
					</div>
				)}

				<div className='space-y-1 sm:space-y-1.5'>
					<div className='flex items-center gap-2'>
						<div className='w-16 h-4 bg-gray-200 rounded animate-pulse' />
						<div className='w-20 h-5 bg-gray-200 rounded animate-pulse' />
					</div>

					<div className='flex items-center gap-1 text-xs sm:text-sm md:text-xs lg:text-sm'>
						<div className='w-3 h-3 sm:w-4 sm:h-4 md:w-3 md:h-3 lg:w-4 lg:h-4 bg-gray-200 rounded-full animate-pulse' />
						<div className='w-20 h-3 bg-gray-200 rounded animate-pulse' />
					</div>
				</div>

				<div className='flex items-center justify-between text-xs sm:text-sm md:text-xs lg:text-sm'>
					<div className='flex items-center gap-1'>
						<div className='w-2 h-2 sm:w-2.5 sm:h-2.5 bg-gray-200 rounded-full animate-pulse' />
						<div className='w-16 h-3 bg-gray-200 rounded animate-pulse' />
					</div>
				</div>
			</div>
		</div>
	)
}

interface ProductGridSkeletonProps {
	count?: number
	className?: string
	showVariants?: boolean
	viewMode?: 'grid' | 'list'
}

const ProductGridSkeleton: React.FC<ProductGridSkeletonProps> = ({
	count = 8,
	className = '',
	showVariants = true,
	viewMode = 'grid',
}) => {
	return (
		<div
			className={`grid transition-all duration-300 ${
				viewMode === 'grid'
					? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-5'
					: 'grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5'
			} ${className}`}
		>
			{[...Array(count)].map((_, index) => (
				<div
					key={index}
					className='transform transition-all duration-500 opacity-70 scale-95'
				>
					<ProductCardSkeleton
						showVariants={showVariants && index % 3 !== 0}
						className='h-full'
					/>
				</div>
			))}
		</div>
	)
}

export { ProductCardSkeleton, ProductGridSkeleton }
export default ProductCardSkeleton
